import React, { useEffect, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CFormSelect,
  CFormInput,
  CButton,
  CModal,
  CFormLabel,
  CForm,
} from "@coreui/react";
import { useDispatch, useSelector } from "react-redux";

import AppLoader from "../../components/seller/AppLoader";
import AppToast from "../../components/seller/AppToast";
import { formatCurrency, formatPrice } from "../../utils/price";

import {
  approveRequest,
  fetchOutOfRange,
  rejectRequest,
} from "../../features/seller/sellerSlice";
import { capitalizeFirst } from "../../utils/stringUtils";
import { timeAgo } from "../../utils/timeAgo";

import { useNavigate } from "react-router-dom";

const RangeRequested = () => {
  const dispatch = useDispatch();

  const {
    data = [],
    loading,
    error,
  } = useSelector((state) => state.buyer.outOfRange);
  const { approveRequest: approveState, rejectRequest: rejectState } =
    useSelector((state) => state.seller);

  const [search, setSearch] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);

  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState("info");
  const [toastMessage, setToastMessage] = useState("");
  const [actionModal, setActionModal] = useState({
    open: false,
    type: null, // "accept" | "reject"
    row: null,
  });
  console.log('Data',data)
  const [extraDeliveryCharge, setExtraDeliveryCharge] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(fetchOutOfRange())
      .unwrap()
      .catch((err) => {
        setToastType("danger");
        setToastMessage(err || "Failed to fetch range requests");
        setToastVisible(true);
      });
  }, [dispatch]);

  /* ---------------- HELPERS ---------------- */
  const safe = (v) => (v === null || v === undefined || v === "" ? "-" : v);

  /* ---------------- FILTER + PAGINATION ---------------- */
  const filtered = data.filter((r) =>
    (r.order_id || "").toString().includes(search)
  );

  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filtered.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filtered.length / entriesPerPage);

  const fields = [
    "#",
    "Order ID",
    "Product",
    "Seller",
    "Distance (km)",
    "Allowed Radius (km)",
    "Extra Charge (AUD)",
    "Status",
    // "Acceptance Status",
    "Created At",
    "Seller Responded",
    "Buyer Responded",
    "Actions",
  ];

  const formatStatus = (status) => {
    if (!status) return "-";

    if (status === "pending_seller") return "Pending";

    return capitalizeFirst(status.replace("_", " "));
  };

  const handleApprove = (row) => {
    setExtraDeliveryCharge(row.extra_delivery_charge || "");
    setErrors({});
    setActionModal({
      open: true,
      type: "accept",
      row,
    });
  };

  const resetModal = () => {
    setActionModal({ open: false, type: null, row: null });
    setExtraDeliveryCharge("");
    setErrors({});
  };

  const confirmReject = () => {
    if (!actionModal.row) return; // safety guard

    dispatch(rejectRequest(actionModal.row.id))
      .unwrap()
      .then(() => {
        setToastType("success");
        setToastMessage("Request rejected successfully");
        setToastVisible(true);

        resetModal();
        dispatch(fetchOutOfRange());
      })
      .catch((err) => {
        setToastType("danger");
        setToastMessage(err || "Failed to reject request");
        setToastVisible(true);
      });
  };

  const handleSubmitAction = () => {
    if (actionModal.type === "accept") {
      if (!extraDeliveryCharge || Number(extraDeliveryCharge) <= 0) {
        setErrors({
          extra_delivery_charge: "Extra delivery charge is required",
        });
        return;
      }

      dispatch(
        approveRequest({
          id: actionModal.row.id,
          data: {
            extra_delivery_charge: Number(extraDeliveryCharge),
          },
        })
      )
        .unwrap()
        .then(() => {
          setToastType("success");
          setToastMessage("Request approved successfully");
          setToastVisible(true);

          resetModal();
          dispatch(fetchOutOfRange());
        })
        .catch((err) => {
          setToastType("danger");
          setToastMessage(err || "Failed to approve request");
          setToastVisible(true);
        });
    }

    if (actionModal.type === "reject") {
      confirmReject();
    }
  };


  // Navigation hook for row click offer detail
    const navigate = useNavigate();
  
    const handleRowClick = (orderId) => {
      navigate(`/seller/order-detail/${orderId}`);
    };
    

  if (loading) return <AppLoader />;

  return (
    <>
      <div className="container-fluid px-3">
        <CRow className="mt-4 mb-3">
          <CCol>
            <h4 className="mb-0">Delivery Requests</h4>
          </CCol>
        </CRow>
      </div>

      <CCard className="mb-4">
        <CCardHeader>
          <CRow className="mb-2">
            <CCol>
              <h6 className="text-muted mb-0">
                Out of range delivery requests
              </h6>
            </CCol>
          </CRow>

          <hr className="my-2" />

          <CRow className="align-items-center">
            <CCol md={6}>
              Show{" "}
              <CFormSelect
                size="sm"
                value={entriesPerPage}
                onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                style={{ display: "inline-block", width: "auto" }}
                options={[
                  { label: "5", value: "5" },
                  { label: "10", value: "10" },
                  { label: "25", value: "25" },
                ]}
              />{" "}
              entries
            </CCol>

            <CCol md={6} className="text-end">
              Search:{" "}
              <CFormInput
                type="text"
                size="sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ display: "inline-block", width: "200px" }}
                placeholder="Order ID"
              />
            </CCol>
          </CRow>
        </CCardHeader>

        <CCardBody>
          <CTable hover responsive bordered>
            <CTableHead color="light">
              <CTableRow>
                {fields.map((h) => (
                  <CTableHeaderCell key={h} align="center">
                    {h}
                  </CTableHeaderCell>
                ))}
              </CTableRow>
            </CTableHead>

            <CTableBody>
              {currentData.map((row, index) => {
                const canTakeAction = row.status === "pending_seller";
                return (
                  <CTableRow key={row.id} style={{ cursor: "pointer" }} onClick={() => handleRowClick(row.order_id)}>
                    <CTableDataCell align="center">
                      {indexOfFirst + index + 1}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {safe(row.order_id)}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {safe(row.order?.product?.name)}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {safe(row.seller?.name)}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {safe(row.distance_km)}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {safe(row.allowed_radius_km)}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {formatCurrency(row.extra_delivery_charge)}
                      {/* {row.extra_delivery_charge
                        ? `A$ ${formatPrice (row.extra_delivery_charge)}`
                        : "-"} */}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {row.status ? (
                        <span
                          className={`badge bg-${
                            row.status === "pending_seller"
                              ? "warning"
                              : row.status === "approved"
                              ? "success"
                              : "danger"
                          }`}
                        >
                          {formatStatus(row.status)}
                        </span>
                      ) : (
                        "-"
                      )}
                    </CTableDataCell>
                    {/* <CTableDataCell align="center">
                    {row.buyer_acceptance ? (
                      <span
                        className={`badge bg-${
                          row.buyer_acceptance === "accepted"
                            ? "success"
                            : row.buyer_acceptance === "rejected"
                            ? "danger"
                            : "warning"
                        }`}
                      >
                        {capitalizeFirst(row.buyer_acceptance)}
                      </span>
                    ) : (
                      "-"
                    )}
                  </CTableDataCell> */}
                    <CTableDataCell align="center">
                      {timeAgo(row.created_at)}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {timeAgo(row.seller_responded_at)}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {timeAgo(row.buyer_responded_at)}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {canTakeAction ? (
                        <>
                          <CButton
                            size="sm"
                            color="success"
                            className="me-1"
                            onClick={(e) =>{
                              e.stopPropagation()  
                              handleApprove(row)}}
                          >
                            Accept
                          </CButton>

                          <CButton
                            size="sm"
                            color="danger"
                            onClick={(e) =>{
                              e.stopPropagation();
                              setActionModal({
                                open: true,
                                type: "reject",
                                row,
                              })}
                            }
                          >
                            Reject
                          </CButton>
                        </>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                );
              })}

              {currentData.length === 0 && (
                <CTableRow>
                  <CTableDataCell
                    colSpan={fields.length}
                    className="text-center text-muted"
                  >
                    No requests found.
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>

          {/* Pagination */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              Showing {indexOfFirst + 1} to{" "}
              {Math.min(indexOfLast, filtered.length)} of {filtered.length}{" "}
              entries
            </div>

            <div>
              <CButton
                size="sm"
                color="light"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                className="me-2"
              >
                Prev
              </CButton>

              {[...Array(totalPages)].map((_, i) => (
                <CButton
                  key={i}
                  size="sm"
                  color={currentPage === i + 1 ? "primary" : "light"}
                  onClick={() => setCurrentPage(i + 1)}
                  className="me-2"
                >
                  {i + 1}
                </CButton>
              ))}

              <CButton
                size="sm"
                color="light"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </CButton>
            </div>
          </div>
        </CCardBody>
      </CCard>

      <CModal
        visible={actionModal.open}
        onClose={resetModal}
        alignment="center"
      >
        <CCard className="m-0">
          <CCardBody>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                {actionModal.type === "accept"
                  ? "Accept Request"
                  : "Reject Request"}
              </h5>

              <CButton color="light" className="p-0" onClick={resetModal}>
                <span aria-hidden="true">&times;</span>
              </CButton>
            </div>

            {/* Message */}
            {actionModal.type === "accept" ? (
              <p className="text-muted mb-3">
                Please enter the extra delivery charge to accept this request.
              </p>
            ) : (
              <p className="text-danger mb-3">
                Are you sure you want to reject this request? This action cannot
                be undone.
              </p>
            )}

            {/* Form */}
            {actionModal.type === "accept" && (
              <CForm>
                <CFormLabel className="fw-semibold">
                  Extra Delivery Charge (AUD) *
                </CFormLabel>
                <CFormInput
                  type="number"
                  min={0}
                  value={extraDeliveryCharge}
                  onChange={(e) => setExtraDeliveryCharge(e.target.value)}
                  invalid={!!errors.extra_delivery_charge}
                  placeholder="Enter extra delivery charge"
                />
                {errors.extra_delivery_charge && (
                  <div className="text-danger small mt-1">
                    {errors.extra_delivery_charge}
                  </div>
                )}
              </CForm>
            )}

            {/* Footer */}
            <div className="d-flex justify-content-end mt-4">
              <CButton color="secondary" className="me-2" onClick={resetModal}>
                Cancel
              </CButton>

              <CButton
                color={actionModal.type === "accept" ? "success" : "danger"}
                disabled={
                  actionModal.type === "accept"
                    ? approveState.loading
                    : rejectState.loading
                }
                onClick={handleSubmitAction}
              >
                {actionModal.type === "accept"
                  ? approveState.loading
                    ? "Processing..."
                    : "Confirm"
                  : rejectState.loading
                  ? "Rejecting..."
                  : "Yes, Reject"}
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      </CModal>

      <AppToast
        visible={toastVisible}
        setVisible={setToastVisible}
        type={toastType}
        message={toastMessage}
      />
    </>
  );
};

export default RangeRequested;
